import { useCallback, useEffect } from 'react'

import {
  EDITOR_DOUBLE_CLICK_INTERVAL,
  EDITOR_DOUBLE_CLICK_POS_THRESHOLD,
  EDITOR_DRAG_DETECTION_THRESHOLD,
} from '@editor/config/constants'
import { CanvasConfigV2 } from '@editor/interfaces/editor'
import { matchesKey } from '@editor/services/Shortcuts'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import {
  setAdjustingElementId,
  setCurrentAnchorPosition,
  setIsDragging,
  setSelectedElementIds,
} from '@editor/store/tool'
import mixpanel from 'mixpanel-browser'

import { useBlueprintEditorContext } from '../contexts/BlueprintEditorContext'
import { removeDrawings } from '../store/drawings'
import usePointSelection from './usePointSelection'
import useProjectionPlane from './useProjectionPlane'

let onDownData: { position: number[]; timestamp: number } | undefined
let lastMouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined
let tripleClickInitiatedTimes: Date[] = []

const useCanvasEvents = (
  toolHooks: {
    key: string
    config: CanvasConfigV2
  }[],
) => {
  // Contexts
  const { isAdjustingRef } = useBlueprintEditorContext()

  // Hooks
  const { getIntersection } = usePointSelection()
  const { projectionPlane } = useProjectionPlane()

  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const adjustingElementId = useToolUtilSelector((state) => state.adjustingElementId)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)

  /**
   * Handles undo events triggered by document events.
   */
  const handleUndo = useCallback(() => {
    toolHooks.forEach((tool) => {
      if (tool.config?.events?.onUndo) {
        tool.config.events.onUndo()
      }
    })

    // Mixpanel tracking
    mixpanel.track('Undo', { Tool: selectedTool })
  }, [selectedTool, toolHooks])

  const handleEscape = useCallback(() => {
    toolHooks.forEach((tool) => {
      if (tool.config?.events?.onCancel) {
        tool.config.events.onCancel()
      }
    })
  }, [toolHooks])

  const handleDelete = useCallback(() => {
    if (!selectedElementIds.length) return
    dispatch(removeDrawings(selectedElementIds))
  }, [dispatch, selectedElementIds])

  /**
   * Handles mouse down events on the canvas.
   *
   * @param event
   */
  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    onDownData = { position: [event.clientX, event.clientY], timestamp: Date.now() }

    // Execute events for tools
    toolHooks.forEach((tool) => tool.config?.events?.onMouseDown && tool.config.events.onMouseDown(event))
  }

  /**
   * Handles mouse move events on the canvas.
   *
   * @param event
   */
  const onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    // Update current anchor position
    const point = getIntersection(event, undefined, { plane: projectionPlane || undefined })
    if (point) {
      dispatch(setCurrentAnchorPosition(point))
    }

    // Execute events for tools
    toolHooks.forEach((tool) => tool.config?.events?.onMouseMove && tool.config.events.onMouseMove(event))
  }

  /**
   * Handles mouse up events on the canvas.
   *
   * @param event
   */
  const onMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    const isAdjusted = isAdjustingRef.current
    isAdjustingRef.current = false
    if (!hoveredElementId.length && !adjustingElementId && event.button === 0) {
      dispatch(setSelectedElementIds([]))
    }
    dispatch(setAdjustingElementId(undefined))

    // Execute events for tools
    toolHooks.forEach((tool) => tool.config?.events?.onRawMouseUp && tool.config.events.onRawMouseUp(event))

    // Ignore non-left mouse button clicks
    if (event.button !== 0) return

    // Check for canvas drag
    const mouseStart = onDownData ? { ...onDownData } : undefined
    onDownData = undefined

    const distanceFromDown = mouseStart
      ? Math.hypot(event.clientX - mouseStart.position[0], event.clientY - mouseStart.position[1])
      : 0
    const isDragged = distanceFromDown > EDITOR_DRAG_DETECTION_THRESHOLD || isAdjusted // consider adjusting as dragging

    const isClickedNearPoint =
      lastMouseEvent !== undefined &&
      Math.abs(lastMouseEvent.clientX - event.clientX) < EDITOR_DOUBLE_CLICK_POS_THRESHOLD &&
      Math.abs(lastMouseEvent.clientY - event.clientY) < EDITOR_DOUBLE_CLICK_POS_THRESHOLD
    const isDoubleClicked =
      tripleClickInitiatedTimes.length === 1 &&
      Date.now() - tripleClickInitiatedTimes[0].valueOf() < EDITOR_DOUBLE_CLICK_INTERVAL &&
      isClickedNearPoint
    const isTripleClicked =
      tripleClickInitiatedTimes.length === 2 &&
      Math.abs(tripleClickInitiatedTimes[1].valueOf() - Date.now()) < EDITOR_DOUBLE_CLICK_INTERVAL &&
      isClickedNearPoint
    lastMouseEvent = event

    if (isTripleClicked) {
      tripleClickInitiatedTimes = []
    } else if (
      tripleClickInitiatedTimes.length === 1 &&
      Math.abs(tripleClickInitiatedTimes[0].valueOf() - Date.now()) < EDITOR_DOUBLE_CLICK_INTERVAL
    ) {
      tripleClickInitiatedTimes = [tripleClickInitiatedTimes[0], new Date()]
    } else {
      tripleClickInitiatedTimes = [new Date()]
    }

    // Execute events for tools
    toolHooks.forEach(
      (tool) =>
        tool.config?.events?.onMouseUp &&
        tool.config.events.onMouseUp(event, {
          isDoubleClicked,
          isTripleClicked,
          isDragged,
        }),
    )

    dispatch(setIsDragging(false))
  }

  /**
   * Handle key up events.
   *
   * We used keydown instead of keyup due to a bug in Mac browsers where the keyup
   * for the intended key (e.g., 'z') is not triggered when a modifier key (Ctrl/Cmd) is held down.
   *
   * @param e - The keyboard event.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // on Ctrl/Cmd + Z, handle undo
      if (matchesKey(e, { key: 'z', ctrl: true }, { fallbackToMeta: true })) {
        handleUndo()
      } else if (matchesKey(e, { key: 'Escape' })) {
        handleEscape()
      } else if (matchesKey(e, { key: 'Delete' })) {
        handleDelete()
      }
    },
    [handleUndo, handleEscape, handleDelete],
  )

  /**
   * Undo is based on document events.
   */
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('editor:tool-undo', handleUndo)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('editor:tool-undo', handleUndo)
    }
  }, [handleUndo, handleKeyDown])

  return {
    onMouseDown,
    onMouseMove,
    onMouseUp,
  }
}

export default useCanvasEvents
